import {
  SearchableSelectProps,
  SearchableSelect,
  SelectItem,
} from "@certn/cascadia-react";
import { slugifyPath } from "@certn/utils";
import { useContext } from "react";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { SummaryItem } from "../components/SummaryItem";
import {
  CascadiaOneOfProps,
  withCascadiaOneOfProps,
} from "../wrappers/withCascadiaOneOfProps";

export function SearchableSelectControl(
  props: CascadiaOneOfProps & Pick<SearchableSelectProps<object>, "isLoading">
) {
  const { items, path } = props;

  const { isSummaryForPath, formID } = useContext(CascadiaJsonFormContext);

  if (
    isSummaryForPath?.(path) &&
    // some fields are displayed as readonly text boxes rather than summary items
    !props.options?.renderSummaryAsReadOnly
  ) {
    const selectedOption = items.find((item) => item.value === props.data);

    return (
      <SummaryItem label={props.label}>
        {selectedOption?.label || props.data}
      </SummaryItem>
    );
  }

  return (
    <SearchableSelect
      placeholder={props.placeholder}
      description={props.description}
      errors={props.errors}
      isDisabled={!props.enabled || isSummaryForPath?.(path)}
      isInvalid={props.invalid}
      isLoading={props.isLoading}
      isRequired={props.required}
      items={items}
      label={props.label}
      menuTrigger="focus"
      name={props.path}
      onBlur={props.setTouched}
      onSelectionChange={(value) => {
        // Changing null/blank string to undefined triggers the "is required" schema validation error,
        // rather than a "must be string"/"must be at least 1 character" message
        if (!value) props.setValue(undefined);
        else props.setValue(value);
      }}
      selectedKey={props.data ?? ""}
      testID={
        formID
          ? `${formID}-${slugifyPath(props.path)}-searchable-select`
          : `${slugifyPath(props.path)}-searchable-select`
      }
      options={{ ...props.options }}
    >
      {(item) => (
        <SelectItem
          key={item.value}
          id={item.value}
          textValue={item.label}
          data-testid={
            formID
              ? `${formID}-${slugifyPath(props.path)}-searchable-item-${
                  item.value
                }`
              : `${slugifyPath(props.path)}-searchable-item${item.value}`
          }
        >
          {item.label}
        </SelectItem>
      )}
    </SearchableSelect>
  );
}

SearchableSelectControl.displayName = "SearchableSelectControl";

export const SearchableSelectControlRenderer = withCascadiaOneOfProps(
  SearchableSelectControl
);
